// Unified filtering and pagination types for framework app
// These types support URL-first architecture with Redux sync

export type FilterableEntity = "backtests" | "executions" | "jobs" | "optimizations";

// Common pagination state
export interface PaginationState {
  page: number; // Current page (0-indexed)
  perPage: number; // Items per page
}

// Run filters (backtests & executions)
export interface RunFilters {
  active: boolean;
  name: string | null;
  id: number | null;
  jobId: number | null;
}

// Job filters
export interface JobFilters {
  hiddenJobTypes: string[]; // Array for Redux serializability
  status: string | null;
  dateRange: { from: string | null; to: string | null };
  dateField: "start_ts" | "complete_ts";
  workerId: string | null;
}

// Optimization filters (for future use)
export interface OptimizationFilters {
  status: string | null;
  name: string | null;
}

// Combined filter state per entity
export interface EntityFilterState<T> {
  filters: T;
  pagination: PaginationState;
}

// Redux state for all filterable entities
export interface EntityFiltersState extends Record<FilterableEntity, EntityFilterState<unknown>> {
  backtests: EntityFilterState<RunFilters>;
  executions: EntityFilterState<RunFilters>;
  jobs: EntityFilterState<JobFilters>;
  optimizations: EntityFilterState<OptimizationFilters>;
}

// Default filter values for each entity
export const getDefaultRunFilters = (): RunFilters => ({
  active: false, // Default for backtests
  name: null,
  id: null,
  jobId: null,
});

export const getDefaultJobFilters = (): JobFilters => ({
  hiddenJobTypes: ["cache"],
  status: null,
  dateRange: { from: null, to: null },
  dateField: "start_ts",
  workerId: null,
});

export const getDefaultOptimizationFilters = (): OptimizationFilters => ({
  status: null,
  name: null,
});

// Default pagination state
export const getDefaultPagination = (): PaginationState => ({
  page: 0,
  perPage: 10, // Will be overridden by entity config
});

// Default entity filter states
export const getDefaultEntityFilters = (): EntityFiltersState => ({
  backtests: {
    filters: getDefaultRunFilters(),
    pagination: getDefaultPagination(),
  },
  executions: {
    filters: { ...getDefaultRunFilters(), active: true },
    pagination: getDefaultPagination(),
  },
  jobs: {
    filters: getDefaultJobFilters(),
    pagination: getDefaultPagination(),
  },
  optimizations: {
    filters: getDefaultOptimizationFilters(),
    pagination: getDefaultPagination(),
  },
});
